/*
 * listview/selectors.js
 *
 * Selectors provide different views of state.
 */
import orderBy from "lodash/orderBy";
import { createSelector } from "reselect";

import policySelectors from "../policies/selectors";
import { allResourceTypeLabel } from "../shared/utils";

const listTablePoliciesSelectors = {};

/**
 * Returns list of policies formatted to populate list view tables.
 * @returns {Array}
 */
listTablePoliciesSelectors.get = createSelector(
  [policySelectors.policies.getWithFullDetails],
  policies => {
    const formattedPolicies = policies.map(policy => {
      const {
        $uri,
        group,
        user,
        role,
        product,
        service,
        resource,
        resourceType
      } = policy;
      let entityLabel = "-";
      let entityUri = "";
      let entityDomain;

      if (group) {
        entityLabel = group.name;
        entityDomain = group.domain;
        entityUri = group.$uri;
      } else if (user) {
        entityLabel = user.username;
        entityDomain = user.domain;
        entityUri = user.$uri;
      }

      let serviceLabel = product ? product.name : "-";
      if (policy.service) {
        serviceLabel = `${service.name} (${serviceLabel})`;
      }

      let resourceLabel = resource ? resource.name : "-";
      if (resourceType && !resource.identifier) {
        resourceLabel = allResourceTypeLabel(resourceType.name);
      } else if (resourceType) {
        resourceLabel = `${resourceLabel} (${resourceType.name})`;
      }

      const roleLabel = role ? role.name : "-";

      return {
        $uri,
        entity: {
          $uri: entityUri,
          type: group ? "group" : "user",
          label: entityLabel,
          domain: entityDomain
        },
        service: {
          $uri: service && service.$uri,
          name: service && service.name,
          label: serviceLabel
        },
        resource: {
          $uri: resource && resource.$uri,
          type: resourceType && resourceType.$uri,
          label: resourceLabel
        },
        role: {
          $uri: role && role.$uri,
          label: roleLabel
        }
      };
    });

    return formattedPolicies.filter(policy => policy.$uri);
  }
);

/**
 * Returns list table policies filtered by non-admins
 * @returns {Array}
 */
listTablePoliciesSelectors.getNonAdmins = createSelector(
  [listTablePoliciesSelectors.get],
  policies =>
    policies.filter(policy => {
      if (policy.service && policy.service.$uri === "/api/rbac/v1/service/0") {
        return false;
      }
      return true;
    })
);

/**
 * Returns list table policies filtered by either entity, service, or role filter
 * @returns {Array}
 */
listTablePoliciesSelectors.getFiltered = createSelector(
  [
    listTablePoliciesSelectors.getNonAdmins,
    policySelectors.users.getFilterTerm,
    policySelectors.services.getFilterTerm,
    policySelectors.roles.getFilterTerm
  ],
  (policies, entitiesFilter, servicesFilter, rolesFilter) => {
    if (entitiesFilter) {
      return policies.filter(policy =>
        policy.entity.label.toLowerCase().includes(entitiesFilter)
      );
    }
    if (servicesFilter) {
      return policies.filter(policy =>
        policy.service.label.toLowerCase().includes(servicesFilter)
      );
    }
    if (rolesFilter) {
      return policies.filter(policy =>
        policy.role.label.toLowerCase().includes(rolesFilter)
      );
    }
    return policies;
  }
);

/**
 * Returns list table policies ordered by group/user.
 * @returns {Array}
 */
listTablePoliciesSelectors.getByGroup = createSelector(
  [listTablePoliciesSelectors.getFiltered],
  policies =>
    orderBy(
      policies,
      [
        policy => policy.entity.type,
        policy => policy.entity.label,
        policy => policy.service.label,
        policy => policy.resource.label
      ],
      ["asc", "asc", "asc", "asc"]
    )
);

/**
 * Returns list table policies ordered by product/service name.
 * @returns {Array}
 */
// export const getListTablePoliciesByService = createSelector(
listTablePoliciesSelectors.getByService = createSelector(
  [listTablePoliciesSelectors.getFiltered],
  policies =>
    orderBy(
      policies,
      [
        policy => policy.service.label,
        policy => policy.resource.label,
        policy => policy.role.label,
        policy => policy.entity.type,
        policy => policy.entity.label
      ],
      ["asc", "asc", "asc", "asc", "asc"]
    )
);

/**
 * Returns list table policies ordered by role name.
 * @returns {Array}
 */
listTablePoliciesSelectors.getByRole = createSelector(
  [listTablePoliciesSelectors.getFiltered],
  policies =>
    orderBy(
      policies,
      [
        policy => policy.role.label,
        policy => policy.service.label,
        policy => policy.resource.label,
        policy => policy.entity.type,
        policy => policy.entity.label
      ],
      ["asc", "asc", "asc", "asc", "asc"]
    )
);

/**
 * Returns list table policies ordered by role name for the current role.
 * @returns {Array}
 */
listTablePoliciesSelectors.getByCurrentRole = createSelector(
  [listTablePoliciesSelectors.getByGroup, policySelectors.roles.getCurrent],
  (policies, currentRole) => {
    if (currentRole) {
      return policies.filter(policy => policy.role.$uri === currentRole.$uri);
    }
    return policies;
  }
);

/**
 * Returns list table policies filtered by current group/user.
 * @returns {Array}
 */
listTablePoliciesSelectors.getByCurrentEntity = createSelector(
  [
    listTablePoliciesSelectors.get,
    policySelectors.groups.getCurrent,
    policySelectors.users.getCurrent
  ],
  (policies, currentGroup, currentUser) => {
    if (!(currentGroup || currentUser)) {
      return [];
    }
    const currentEntity = currentGroup || currentUser;
    const orderedPolicies = orderBy(
      policies,
      [
        policy => policy.entity.type,
        policy => policy.entity.label,
        policy => policy.service.label,
        policy => policy.resource.label
      ],
      ["asc", "asc", "asc", "asc"]
    );
    return orderedPolicies.filter(
      policy => policy.entity.$uri === currentEntity.$uri
    );
  }
);

/**
 * Returns list table policies attached to self-referential RBAC service,
 * ordered by entity type then entity label
 * @param {Object} state
 * @returns {Object[]} list table policy
 */
listTablePoliciesSelectors.getAdmins = createSelector(
  [listTablePoliciesSelectors.get],
  policies => {
    const filteredPolicies = policies.filter(policy => {
      if (policy.service) {
        return policy.service.$uri === "/api/rbac/v1/service/0";
      }
      return false;
    });

    const order = orderBy(
      filteredPolicies,
      [policy => policy.entity.type, policy => policy.entity.label],
      ["asc", "asc"]
    );
    return order;
  }
);

/**
 * Returns RBAC admin list table policies ordered by role name for the current role.
 * @returns {Array}
 */
listTablePoliciesSelectors.getAdminsByCurrentRole = createSelector(
  [listTablePoliciesSelectors.getAdmins, policySelectors.roles.getCurrent],
  (policies, currentRole) => {
    if (currentRole) {
      return policies.filter(policy => policy.role.$uri === currentRole.$uri);
    }
    return policies;
  }
);

export default listTablePoliciesSelectors;
